import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  MapPin,
  Calendar,
  Building2,
  CheckCircle,
  Archive,
  ArchiveRestore,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "../App";
import { toast } from "sonner";
import { api } from "../services/apiClient";
import { MICROCOPY } from "../constants/microcopy";
import BackNavigationButton from "../components/navigation/BackNavigationButton";

const Inquiries = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const response = await api.get("/contact");

      let data = null;
      try {
        data = await response.json();
      } catch (_parseError) {
        data = null;
      }

      if (!response.ok) {
        const detail = String(data?.detail || data?.message || "").toLowerCase();
        const isNoData =
          response.status === 404 ||
          ((detail.includes("no hay") || detail.includes("sin")) &&
            (detail.includes("consulta") || detail.includes("datos")));

        if (isNoData) {
          setInquiries([]);
          return;
        }

        throw new Error(data?.detail || data?.message || "Error al cargar consultas");
      }

      setInquiries(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching inquiries:", error);
      toast.error(error?.message || "Error al cargar consultas");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (inquiryId) => {
    try {
      await api.patchQuery(`/contact/${inquiryId}/mark-read`);
      toast.success("Marcado como leído");
      fetchInquiries();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const toggleArchive = async (inquiryId) => {
    try {
      const response = await api.patchQuery(`/contact/${inquiryId}/archive`);
      const data = await response.json();
      toast.success(
        data.is_archived ? "Consulta archivada" : "Consulta restaurada",
      );
      fetchInquiries();
    } catch (error) {
      toast.error("Error al archivar");
    }
  };

  const deleteInquiry = async (inquiryId) => {
    if (
      !window.confirm(
        "¿Estás seguro de que deseas eliminar esta consulta permanentemente?",
      )
    )
      return;

    try {
      const response = await api.delete(`/contact/${inquiryId}`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Error al eliminar");
      }

      toast.success("Consulta eliminada permanentemente");
      fetchInquiries();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-MX", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-muted">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-6 md:mb-8">
            <div className="mb-4">
              <BackNavigationButton dataTestId="inquiries-back" />
            </div>
            <h1
              className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight"
              style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
              data-testid="inquiries-title"
            >
              Consultas Recibidas
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Mensajes de interesados en tus propiedades
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : inquiries.length === 0 ? (
            <div className="bg-background rounded-lg p-12 text-center">
              <Mail className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">
                {MICROCOPY.emptyStates.inquiries.title}
              </h2>
              <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                {MICROCOPY.emptyStates.inquiries.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate("/dashboard")}>
                  {MICROCOPY.emptyStates.inquiries.primaryCta}
                </Button>
                <Button variant="outline" onClick={() => navigate("/add-property")}>
                  {MICROCOPY.emptyStates.inquiries.secondaryCta}
                </Button>
              </div>
            </div>
          ) : (
            <Tabs defaultValue="active" className="w-full">
              <TabsList className="grid w-full md:w-auto grid-cols-2 mb-6">
                <TabsTrigger value="active" data-testid="tab-active">
                  Activas ({inquiries.filter((i) => !i.is_archived).length})
                </TabsTrigger>
                <TabsTrigger value="archived" data-testid="tab-archived">
                  Archivadas ({inquiries.filter((i) => i.is_archived).length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="active">
                <div className="space-y-4">
                  {inquiries.filter((i) => !i.is_archived).length === 0 ? (
                    <div className="bg-background rounded-lg p-12 text-center">
                      <p className="text-muted-foreground">
                        No hay consultas activas. Las archivadas siguen disponibles en la pestaña de historial.
                      </p>
                    </div>
                  ) : (
                    inquiries
                      .filter((i) => !i.is_archived)
                      .map((inquiry) => (
                        <motion.div
                          key={inquiry.id || inquiry._id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`bg-background rounded-lg p-4 md:p-6 border-l-4 ${
                            inquiry.is_read
                              ? "border-gray-300"
                              : "border-primary"
                          }`}
                          data-testid={`inquiry-${inquiry.id || inquiry._id}`}
                        >
                          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3
                                  className="text-lg font-bold"
                                  style={{ fontFamily: "Manrope, sans-serif" }}
                                >
                                  {inquiry.name}
                                </h3>
                                {!inquiry.is_read && (
                                  <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full">
                                    Nueva
                                  </span>
                                )}
                              </div>

                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-muted-foreground mb-3">
                                <span className="flex items-center">
                                  <Mail className="w-4 h-4 mr-1" />
                                  {inquiry.email}
                                </span>
                                {inquiry.phone && (
                                  <span className="flex items-center">
                                    <Phone className="w-4 h-4 mr-1" />
                                    {inquiry.phone}
                                  </span>
                                )}
                                <span className="flex items-center">
                                  <Calendar className="w-4 h-4 mr-1" />
                                  {formatDate(inquiry.created_at)}
                                </span>
                              </div>

                              {inquiry.property_title && (
                                <div className="bg-muted/50 rounded-lg p-3 mb-3">
                                  <p className="text-xs text-muted-foreground mb-1">
                                    Propiedad de interés:
                                  </p>
                                  <div className="flex items-center gap-2">
                                    <Building2 className="w-4 h-4 text-accent" />
                                    <p className="font-semibold">
                                      {inquiry.property_title}
                                    </p>
                                  </div>
                                  {inquiry.property_address && (
                                    <p className="text-sm text-muted-foreground flex items-center mt-1">
                                      <MapPin className="w-3 h-3 mr-1" />
                                      {inquiry.property_address}
                                    </p>
                                  )}
                                  {inquiry.property_price_mxn && (
                                    <p className="text-sm font-bold mt-1">
                                      $
                                      {inquiry.property_price_mxn.toLocaleString()}{" "}
                                      MXN
                                    </p>
                                  )}
                                </div>
                              )}

                              <div className="bg-muted/30 rounded-lg p-3">
                                <p className="text-xs text-muted-foreground mb-1">
                                  Mensaje:
                                </p>
                                <p className="text-sm">{inquiry.message}</p>
                              </div>

                              {(inquiry.budget_range ||
                                inquiry.purchase_timeline ||
                                inquiry.intended_use) && (
                                <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-3 mt-3">
                                  <p className="text-xs font-semibold text-yellow-800 mb-2">
                                    📊 Calificación del Lead
                                  </p>
                                  {inquiry.budget_range && (
                                    <p className="text-xs text-yellow-900 mb-1">
                                      <strong>💰 Presupuesto:</strong>{" "}
                                      {inquiry.budget_range}
                                    </p>
                                  )}
                                  {inquiry.purchase_timeline && (
                                    <p className="text-xs text-yellow-900 mb-1">
                                      <strong>📅 Horizonte:</strong>{" "}
                                      {inquiry.purchase_timeline}
                                    </p>
                                  )}
                                  {inquiry.intended_use && (
                                    <p className="text-xs text-yellow-900">
                                      <strong>🏠 Uso:</strong>{" "}
                                      {inquiry.intended_use}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>

                            <div className="flex flex-col gap-2">
                              {!inquiry.is_read && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    markAsRead(inquiry.id || inquiry._id)
                                  }
                                  data-testid={`mark-read-${inquiry.id || inquiry._id}`}
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Marcar Leída
                                </Button>
                              )}
                              {inquiry.is_read && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    toggleArchive(inquiry.id || inquiry._id)
                                  }
                                  data-testid={`archive-${inquiry.id || inquiry._id}`}
                                >
                                  <Archive className="w-4 h-4 mr-2" />
                                  Archivar
                                </Button>
                              )}
                              <Button
                                size="sm"
                                onClick={() =>
                                  navigate(`/property/${inquiry.property_id}`)
                                }
                                data-testid={`view-property-${inquiry.id || inquiry._id}`}
                              >
                                Ver Propiedad
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      ))
                  )}
                </div>
              </TabsContent>

              <TabsContent value="archived">
                <div className="space-y-4">
                  {inquiries.filter((i) => i.is_archived).length === 0 ? (
                    <div className="bg-background rounded-lg p-12 text-center">
                      <Archive className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        No hay consultas archivadas todavía.
                      </p>
                    </div>
                  ) : (
                    inquiries
                      .filter((i) => i.is_archived)
                      .map((inquiry) => (
                        <motion.div
                          key={inquiry.id || inquiry._id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-background rounded-lg p-4 md:p-6 border-l-4 border-gray-300 opacity-75"
                          data-testid={`inquiry-${inquiry.id || inquiry._id}`}
                        >
                          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3
                                  className="text-lg font-bold"
                                  style={{ fontFamily: "Manrope, sans-serif" }}
                                >
                                  {inquiry.name}
                                </h3>
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">
                                  Archivada
                                </span>
                              </div>

                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-muted-foreground mb-3">
                                <span className="flex items-center">
                                  <Mail className="w-4 h-4 mr-1" />
                                  {inquiry.email}
                                </span>
                                {inquiry.phone && (
                                  <span className="flex items-center">
                                    <Phone className="w-4 h-4 mr-1" />
                                    {inquiry.phone}
                                  </span>
                                )}
                                <span className="flex items-center">
                                  <Calendar className="w-4 h-4 mr-1" />
                                  {formatDate(inquiry.created_at)}
                                </span>
                              </div>

                              {inquiry.property_title && (
                                <div className="bg-muted/50 rounded-lg p-3 mb-3">
                                  <p className="text-xs text-muted-foreground mb-1">
                                    Propiedad:
                                  </p>
                                  <p className="font-semibold text-sm">
                                    {inquiry.property_title}
                                  </p>
                                </div>
                              )}

                              <div className="bg-muted/30 rounded-lg p-3">
                                <p className="text-xs text-muted-foreground mb-1">
                                  Mensaje:
                                </p>
                                <p className="text-sm line-clamp-2">
                                  {inquiry.message}
                                </p>
                              </div>
                            </div>

                            <div className="flex flex-col gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  toggleArchive(inquiry.id || inquiry._id)
                                }
                                data-testid={`unarchive-${inquiry.id || inquiry._id}`}
                              >
                                <ArchiveRestore className="w-4 h-4 mr-2" />
                                Restaurar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  navigate(`/property/${inquiry.property_id}`)
                                }
                              >
                                Ver Propiedad
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() =>
                                  deleteInquiry(inquiry.id || inquiry._id)
                                }
                                data-testid={`delete-inquiry-${inquiry.id || inquiry._id}`}
                              >
                                Eliminar
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default Inquiries;
